import * as React from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { Descendant } from "slate";

import { ICannedResponsesProps, replaceCannedResponseTagsWithVariables } from "components/message-box/canned-responses";
import { NEW_TICKET_TEMP_ID, SESSION_STORAGE_DRAFT_KEY } from "constants/tickets/drafts";
import { IWithBreakpointsProps, withBreakpoints } from "helpers/withBreakpoints";
import { TicketStatus } from "interfaces/entities/ticket";
import { getItem } from "services/session-storage";
import { IWithAgentsState, selectAgent, selectLoggedInAgent } from "store/entities/agents";
import { IAgent } from "store/entities/agents/interfaces";
import { selectFeatureToggleReplySuggestions } from "store/entities/license";
import { getTicket } from "store/views/tickets/selectors";

import { Attachment } from "./message-box/legacy/TicketAttachments";
import { TicketMessageBox as LegacyTicketMessageBox } from "./message-box/legacy/TicketMessageBox";
import { TicketMessageBox as RichTextTicketMessageBox } from "./message-box/richtext/TicketMessageBox";

interface IProps {
  isSendingMessage: boolean;
  agentId?: IAgent["id"];
  agentSignature: IAgent["signature"] | null;
  cannedResponsesByShortcuts: ICannedResponsesProps["items"];
  canSendPublicMessage: boolean;
  canSendPrivateMessage: boolean;
  ticketId?: string;
  ticketStatus?: TicketStatus;
  onMessageSend(
    message: Descendant[],
    isPrivate: boolean,
    status: TicketStatus,
    attachments?: File[],
    transactionID?: string,
    richTextPresent?: boolean,
    mentionedAgents?: string[],
    handleClearMessageBox?: () => void,
    usingShortcut?: boolean,
    cannedResponseIds?: string[],
    replySuggestionIds?: string[]
  ): void;
  onDraftSave?(
    message: string | Descendant[],
    messageText: string,
    isPrivate: boolean,
    status: TicketStatus,
    cannedResponseIds?: string[],
    replySuggestionIds?: string[]
  ): void;
  onAttachmentsChange?(attachments: Attachment[]): void;
  transactionID?: string;
  newTicket?: boolean;
  notifyTicketTyping?(ticketId: string): void;
  notifyStopTicketTyping?(ticketId: string): void;
  blockEditForMerged: boolean;
}

interface IOldIEDocument extends Document {
  documentMode?: number;
}

const TicketMessageBox: React.FC<IProps & IWithBreakpointsProps> = props => {
  const { pathname } = useLocation();
  const isNewTicket = pathname.endsWith("add");
  const ticket = useSelector(getTicket);
  const draftTicket = getItem(SESSION_STORAGE_DRAFT_KEY)?.[NEW_TICKET_TEMP_ID];
  const agentId = isNewTicket ? draftTicket?.assigneeId : ticket?.assignment?.agent?.id;
  const agent = useSelector(state => selectAgent(state as IWithAgentsState, agentId));
  const currentAgent = useSelector(selectLoggedInAgent);
  const requester = ticket?.requester;
  const shortID = isNewTicket ? null : ticket?.shortId;
  const agentName = agent?.name ? agent?.name : currentAgent?.name;
  const agentEmail = agent?.email ? agent?.email : currentAgent?.email;
  const requesterName = isNewTicket ? draftTicket?.requesterName : requester?.name;
  const requesterEmail = isNewTicket ? draftTicket?.requesterEmail : requester?.email;
  const featureToggleReplySuggestions = useSelector(selectFeatureToggleReplySuggestions);

  const canneds = React.useMemo(() => {
    const cannedVariablesMap = new Map([
      ["{{ticket.requesterName}}", requesterName],
      ["{{ticket.requesterEmail}}", requesterEmail],
      ["{{ticket.agentName}}", agentName],
      ["{{ticket.agentEmail}}", agentEmail],
      ["{{ticket.shortID}}", shortID],
    ]);

    const cannedVariablesRegExp = new RegExp([...cannedVariablesMap.keys()].join("|"), "g");
    return replaceCannedResponseTagsWithVariables(
      cannedVariablesMap,
      cannedVariablesRegExp,
      props.cannedResponsesByShortcuts
    );
  }, [props.cannedResponsesByShortcuts, agentEmail, agentName, requesterEmail, requesterName, shortID]);

  const isInternetExplorer = !!(document as IOldIEDocument).documentMode;
  const isEdge = /Edge/.test(window.navigator.userAgent);
  const isAndroidMobile = /Android/.test(window.navigator.userAgent);
  const isCompatibleBrowser = !(isInternetExplorer || isEdge || isAndroidMobile);

  return isCompatibleBrowser ? (
    // @ts-expect-error TS(2769): No overload matches this call.
    <RichTextTicketMessageBox
      {...props}
      cannedResponsesByShortcuts={canneds}
      isMutating={false}
      hasReplySuggestions={featureToggleReplySuggestions}
    />
  ) : (
    // @ts-expect-error TS(2769): No overload matches this call.
    <LegacyTicketMessageBox
      {...props}
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      agentSignature={props.agentSignature.text}
      cannedResponsesByShortcuts={canneds}
    />
  );
};

export default withBreakpoints(TicketMessageBox);
